import React from "react";
import { formatRiskLabel, mapRiskToEnglish } from "../../shared";
import { InfoField } from "../../arbre/print/components/InfoField";
import { RiskIndicator } from "../../arbre/print/components/RiskIndicator";
import type { ResolvedItem } from "../../arbre/print/template/types";
import type { EpaysageCtx } from "./registry";

/** Contexte d'un bloc : l'essence imprimée pour les sections « essence » de la fiche détaillée. */
export interface EpaysageBlockCtx {
  essence: any | null;
  index: number;
}

const Photo = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    className="aspect-[4/3] h-auto w-full max-w-[20rem] rounded border border-border object-cover"
  />
);

const RiskValue = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-2">
    <span className="text-[14px]">{formatRiskLabel(label)}</span>
    <RiskIndicator level={mapRiskToEnglish(label)} />
  </span>
);

const DetailPhotos = ({ data }: { data: EpaysageCtx["data"] }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {data.img1 && <Photo src={data.img1} alt="Image 1" />}
    {data.img2 && <Photo src={data.img2} alt="Image 2" />}
    {data.img3 && <Photo src={data.img3} alt="Image 3" />}
  </div>
);

export const EPAYSAGE_WEB_BLOCKS: Record<
  string,
  (
    data: EpaysageCtx["data"],
    ctx: EpaysageBlockCtx,
    item: ResolvedItem,
  ) => React.ReactNode
> = {
  bloc_risque_global: (d) => (
    <div className="mb-1 flex flex-wrap items-center gap-1 p-[2px]">
      <span className="text-sm font-bold text-[#4E5155] print:text-[13px] sm:text-[15px]">
        Risque global de l'espace boisé :{" "}
      </span>
      <div className="p-3">
        <RiskValue label={d.risque?.general} />
      </div>
    </div>
  ),
  bloc_photos: (d) => <DetailPhotos data={d} />,
  bloc_commentaire: (d, _ctx, item) => (
    <InfoField label={item.label} value={d.comments} />
  ),
  bloc_essence_photos: (_d, ctx) => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {ctx.essence?.img1 && (
        <Photo
          src={ctx.essence.img1}
          alt={`Image 1 - essence ${ctx.index + 1}`}
        />
      )}
      {ctx.essence?.img2 && (
        <Photo
          src={ctx.essence.img2}
          alt={`Image 2 - essence ${ctx.index + 1}`}
        />
      )}
    </div>
  ),
  bloc_essence_risque: (_d, ctx, item) => (
    <InfoField
      label={item.label}
      className="flex items-center"
      value={
        <div className="ml-2">
          <RiskValue label={ctx.essence?.risque} />
        </div>
      }
    />
  ),
};

/** Rendu réel des blocs riches pour `HtmlEpaysageTemplate` (l'éditeur garde des cadres). */
export const renderEpaysageWebBlock =
  (data: EpaysageCtx["data"]) =>
  (item: ResolvedItem, ctx: EpaysageBlockCtx = { essence: null, index: 0 }) =>
    EPAYSAGE_WEB_BLOCKS[item.key]?.(data, ctx, item) ?? null;
